import { useEffect } from 'react';
import { LoaderCircle } from 'lucide-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { queryClient } from './api/queryClient';
import { router as browserRouter } from './router/router';
import { useAuthStore } from './store/auth.store';

export interface AppProps {
  /** Defaults to the live app's browser router; overridable (e.g. a memory router) for tests. */
  router?: ReturnType<typeof createBrowserRouter>;
}

function SessionLoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg text-primary">
      <LoaderCircle size={28} className="animate-spin" />
    </div>
  );
}

export function App({ router = browserRouter }: AppProps) {
  const status = useAuthStore((s) => s.status);

  // Silently restore a session from the httpOnly refresh cookie before
  // rendering the authenticated shell — see RequireAuth for the route guard
  // that reads the resulting status. The api client has its own timeout, so
  // this settles within seconds either way; the hard cap is a safety net so a
  // stuck backend can never leave the app parked on the boot spinner.
  useEffect(() => {
    void useAuthStore.getState().initSession();
    const forceUnauthenticated = setTimeout(() => {
      if (useAuthStore.getState().status === 'loading') {
        useAuthStore.getState().clearSession();
      }
    }, 18_000);
    return () => clearTimeout(forceUnauthenticated);
  }, []);

  const booting = status === 'idle' || status === 'loading';

  return <QueryClientProvider client={queryClient}>{booting ? <SessionLoadingScreen /> : <RouterProvider router={router} />}</QueryClientProvider>;
}
